import React, { useState, useRef, useEffect } from 'react';
import { Filter, X, Check } from 'lucide-react';

/**
 * ColumnFilter - Excel-style inline column filter
 * 
 * Props:
 * - type: 'text' | 'number' | 'date' | 'status'
 * - options: Array of { value, label } for status type
 * - value: Current filter value
 * - onChange: Callback when filter changes
 * - placeholder: Placeholder text
 */
const ColumnFilter = ({ 
  type = 'text', 
  options = [], 
  value, 
  onChange,
  placeholder = 'Filter...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localValue, setLocalValue] = useState(value || (type === 'status' ? [] : type === 'number' || type === 'date' ? { min: '', max: '' } : ''));
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync local value with prop
  useEffect(() => {
    if (value !== undefined) {
      setLocalValue(value);
    }
  }, [value]);

  const handleApply = () => {
    onChange?.(localValue);
    setIsOpen(false);
  };

  const handleClear = () => {
    const emptyValue = type === 'status' ? [] : type === 'number' || type === 'date' ? { min: '', max: '' } : '';
    setLocalValue(emptyValue);
    onChange?.(emptyValue);
    setIsOpen(false);
  };

  const handleStatusToggle = (optionValue) => {
    const newValue = localValue.includes(optionValue)
      ? localValue.filter(v => v !== optionValue)
      : [...localValue, optionValue];
    setLocalValue(newValue);
  };

  const hasActiveFilter = () => {
    if (type === 'status') return Array.isArray(localValue) && localValue.length > 0;
    if (type === 'number' || type === 'date') return localValue?.min || localValue?.max;
    return localValue && localValue.length > 0;
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        ref={buttonRef}
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className={`p-0.5 rounded transition-colors ${hasActiveFilter() ? 'text-teal-600' : 'text-slate-500 hover:text-slate-700'}`}
        title="Filter column"
      >
        <Filter className="w-3 h-3" />
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute top-full left-0 mt-1 z-50 bg-white rounded-lg shadow-lg border border-slate-200 min-w-[180px] py-2"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Text Filter */}
          {type === 'text' && (
            <div className="px-2">
              <input
                type="text"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                placeholder={placeholder}
                className="w-full px-2 py-1.5 text-[11px] border border-slate-200 rounded focus:outline-none focus:border-teal-400"
                autoFocus
              />
            </div>
          )}

          {/* Number Filter */}
          {type === 'number' && (
            <div className="px-2 space-y-1.5">
              <input
                type="number"
                value={localValue?.min || ''}
                onChange={(e) => setLocalValue({ ...localValue, min: e.target.value })}
                placeholder="Min"
                className="w-full px-2 py-1.5 text-[11px] border border-slate-200 rounded focus:outline-none focus:border-teal-400"
              />
              <input
                type="number"
                value={localValue?.max || ''}
                onChange={(e) => setLocalValue({ ...localValue, max: e.target.value })}
                placeholder="Max"
                className="w-full px-2 py-1.5 text-[11px] border border-slate-200 rounded focus:outline-none focus:border-teal-400"
              />
            </div>
          )}

          {/* Date Filter */}
          {type === 'date' && (
            <div className="px-2 space-y-1.5">
              <div>
                <label className="text-[10px] text-slate-400 uppercase">From</label>
                <input
                  type="date"
                  value={localValue?.min || ''}
                  onChange={(e) => setLocalValue({ ...localValue, min: e.target.value })}
                  className="w-full px-2 py-1.5 text-[11px] border border-slate-200 rounded focus:outline-none focus:border-teal-400"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 uppercase">To</label>
                <input
                  type="date"
                  value={localValue?.max || ''}
                  onChange={(e) => setLocalValue({ ...localValue, max: e.target.value })}
                  className="w-full px-2 py-1.5 text-[11px] border border-slate-200 rounded focus:outline-none focus:border-teal-400"
                />
              </div>
            </div>
          )}

          {/* Status Filter (Checkbox List) */}
          {type === 'status' && (
            <div className="max-h-[200px] overflow-y-auto">
              {options.map((option) => (
                <label 
                  key={option.value} 
                  className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={localValue.includes(option.value)}
                    onChange={() => handleStatusToggle(option.value)}
                    className="w-3 h-3 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-[11px] text-slate-600">{option.label}</span>
                </label>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-1 px-2 pt-2 mt-2 border-t border-slate-100">
            <button
              onClick={handleClear}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
            <button
              onClick={handleApply}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] text-white bg-teal-600 hover:bg-teal-700 rounded"
            >
              <Check className="w-3 h-3" />
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnFilter;
